import { apiClient, idempotentConfig } from './client';
import type { ApiEnvelope } from './types';

/**
 * Bulk start-runs (OL-29, PROPOSED `contracts/proposed/openapi/PROPOSAL-ol-29-bulk-operations.md`).
 * Upload validates and starts nothing; confirm starts the valid rows in the background.
 */

export type BulkSubjectType = 'INDIVIDUAL' | 'CORPORATE' | 'CONNECTION_GROUP';

export type BulkBatchStatus =
  | 'AWAITING_CONFIRMATION'
  | 'NOTHING_TO_START'
  | 'RUNNING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_FAILURES'
  | 'CANCELLED'
  | 'EXPIRED';

export type BulkRowStatus =
  | 'INVALID'
  | 'VALID'
  | 'DUPLICATE'
  | 'GROUPED'
  | 'QUEUED'
  | 'STARTED'
  | 'FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export interface BulkTemplateColumn {
  name: string;
  need: 'ALWAYS' | 'IF_INVITING' | 'IF_NOT_INVITING' | 'OPTIONAL';
  format: string;
  hint: string;
  aliases: string[];
}

export interface BulkTemplate {
  subjectType: BulkSubjectType;
  columns: BulkTemplateColumn[];
}

export interface BulkBatchCounts {
  invalid: number;
  valid: number;
  duplicate: number;
  grouped: number;
  queued: number;
  started: number;
  failed: number;
  cancelled: number;
  expired: number;
  runsActive: number;
  runsCompleted: number;
  runsFailed: number;
}

export interface BulkBatch {
  batchId: string;
  name: string;
  subjectType: BulkSubjectType;
  flowId: string;
  flowVersion: number;
  status: BulkBatchStatus;
  totalRows: number;
  counts: BulkBatchCounts;
  origin: string;
  createdBy: string;
  createdAt: string;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  finishedAt?: string | null;
  expiresAt: string;
  screenConfigId?: string | null;
  repeatOfBatchId?: string | null;
  repeatReason?: string | null;
  reused: boolean;
}

export interface BulkRowError {
  column: string;
  code: string;
  message: string;
}

export interface BulkRow {
  rowNo: number;
  status: BulkRowStatus;
  reference?: string | null;
  subjectLabel?: string | null;
  entityId?: string | null;
  runId?: string | null;
  runState?: string | null;
  runOutcome?: string | null;
  errorCode?: string | null;
  errors: BulkRowError[];
  leaderRow?: number | null;
  priorBatchId?: string | null;
  priorRowNo?: number | null;
  inviteStatus?: string | null;
  inviteProvider?: string | null;
}

export interface BulkPage<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface BulkUpload {
  flowId: string;
  subjectType: BulkSubjectType;
  name: string;
  screenConfigId?: string;
  locale?: string;
  maxPerGroup?: number;
  repeatReason?: string;
}

/** Validating a large file takes longer than the client's default timeout. */
const UPLOAD_TIMEOUT_MS = 120_000;

export async function listBulkTemplates(): Promise<BulkTemplate[]> {
  const { data } = await apiClient.get<ApiEnvelope<BulkTemplate[]>>('/v1/bulk/templates');
  return data.data;
}

export async function uploadBulkBatch(upload: BulkUpload, file: Blob, idemKey?: string): Promise<BulkBatch> {
  const { data } = await apiClient.post<ApiEnvelope<BulkBatch>>(
    '/v1/bulk/batches',
    file,
    idempotentConfig(idemKey, {
      params: upload,
      timeout: UPLOAD_TIMEOUT_MS,
      headers: { 'Content-Type': 'text/csv' },
    }),
  );
  return data.data;
}

export async function listBulkBatches(page: number, pageSize: number): Promise<BulkPage<BulkBatch>> {
  const { data } = await apiClient.get<ApiEnvelope<BulkPage<BulkBatch>>>('/v1/bulk/batches', {
    params: { page, pageSize },
  });
  return data.data;
}

export async function getBulkBatch(batchId: string): Promise<BulkBatch> {
  const { data } = await apiClient.get<ApiEnvelope<BulkBatch>>(`/v1/bulk/batches/${encodeURIComponent(batchId)}`);
  return data.data;
}

export async function listBulkRows(
  batchId: string,
  page: number,
  pageSize: number,
  status?: BulkRowStatus,
): Promise<BulkPage<BulkRow>> {
  const { data } = await apiClient.get<ApiEnvelope<BulkPage<BulkRow>>>(
    `/v1/bulk/batches/${encodeURIComponent(batchId)}/rows`,
    { params: { page, pageSize, status } },
  );
  return data.data;
}

async function act(batchId: string, action: 'confirm' | 'cancel' | 'retry-failed'): Promise<BulkBatch> {
  const { data } = await apiClient.post<ApiEnvelope<BulkBatch>>(
    `/v1/bulk/batches/${encodeURIComponent(batchId)}/${action}`,
    undefined,
    idempotentConfig(),
  );
  return data.data;
}

export const confirmBulkBatch = (batchId: string) => act(batchId, 'confirm');
export const cancelBulkBatch = (batchId: string) => act(batchId, 'cancel');
export const retryFailedBulkBatch = (batchId: string) => act(batchId, 'retry-failed');

/** Fetches with the bearer, then hands the browser a file. A bare link would carry no token. */
async function saveCsv(path: string, filename: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const downloadBulkTemplate = (type: BulkSubjectType) =>
  saveCsv(`/v1/bulk/templates/${type}/download`, `bulk-template-${type.toLowerCase()}.csv`);

export const downloadBulkReport = (batchId: string) =>
  saveCsv(`/v1/bulk/batches/${encodeURIComponent(batchId)}/report`, `bulk-report-${batchId}.csv`);
